<template>
  <div class="flex h-full flex-col resource-library-body__panel">
    <div class="header">
      <div class="header-section">
        <button
          class="mr-2 inline-flex h-6 items-center justify-center border-0 bg-transparent p-0 text-[1.2rem] font-medium text-[#a5a5a5] transition hover:text-white focus:outline-none"
          data-entity="producer-expand-timeline"
          data-test="producer-curatedContent-expand"
          @click="$emit('update:collapsed', !collapsed)"
        >
          <span
            class="fas fa-angle-double-up block text-[12px] leading-none"
            :class="{ 'fa-flip-vertical': collapsed }"
          />
        </button>

        <p class="title mr-4 flex items-center leading-none">Curated Content</p>

        <button
          class="ml-auto mr-2 inline-flex h-6 items-center justify-center border-0 bg-transparent p-0 text-[#a5a5a5] transition hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          title="Refresh curated content"
          data-entity="producer-curatedContent-reload"
          data-test="producer-curatedContent-reload"
          @click="$emit('reload')"
        >
          <span class="fas fa-redo" />
        </button>
      </div>
    </div>

    <div class="source-types-wrapper">
      <div
        :class="{
          'source-types': true,
          'small-viewport': contentTabOverflow,
        }"
        ref="contentTabs"
        @scroll="handleScroll"
      >
        <button
          v-if="contentTabOverflow && contentTabOverflowButtonsDisabled[0]"
          class="small-viewport__button-left border-0 bg-transparent"
          title="Scroll left"
          data-entity="producer-assets-scrollRight"
          data-test="producer-assets-contentTabs-scrollLeft"
          @click="scrollMenuLeft"
        >
          <span class="fas fa-angle-left" />
        </button>

        <button
          :class="{
            'tab-btn tab-btn--outlined px-[10px] py-[8px] text-[12px] leading-none': true,
            active: contentTab === 'tagboards',
          }"
          data-entity="producer-assets-showTagboards"
          data-test="producer-assets-boardsTab"
          @click="contentTab = 'tagboards'"
        >
          Boards
        </button>

        <button
          v-if="hasShoppable"
          :class="{
            'tab-btn tab-btn--outlined px-[10px] py-[8px] text-[12px] leading-none': true,
            active: contentTab === 'shoppable',
          }"
          data-entity="producer-assets-showShoppable"
          data-test="producer-assets-shoppableTab"
          @click="contentTab = 'shoppable'"
        >
          Shoppable
        </button>

        <button
          v-if="contentTabOverflow && contentTabOverflowButtonsDisabled[1]"
          class="small-viewport__button-right border-0 bg-transparent"
          title="Scroll right"
          data-entity="producer-assets-scrollRight"
          data-test="producer-assets-contentTabs-scrollRight"
          @click="scrollMenuRight"
        >
          <span class="fas fa-angle-right" />
        </button>
      </div>
    </div>

    <div class="resource-library-body__list h-full min-h-0 background-color-inherit">
      <component
        v-if="ready"
        :key="contentTabComponent"
        :is="contentTabComponent"
        :tagboard-id="tagboardId"
        class="loaded background-color-inherit"
        @update:tagboard-id="$emit('update:tagboard-id', $event)"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { defineAsyncComponent } from 'vue';

export default {
  name: 'CuratedContent',

  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
    tagboardId: {
      type: Number,
      default: null,
    },
    ready: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['update:collapsed', 'update:tagboard-id', 'reload'],

  data() {
    return {
      contentTab: 'tagboards',
      contentTabOverflow: false,
      contentTabOverflowButtonsDisabled: [false, false],
    };
  },

  computed: {
    ...mapGetters([
      'hasShoppable',
    ]),

    contentTabComponent() {
      switch (this.contentTab) {
        case 'tagboards':
          return defineAsyncComponent(() => import('./TagboardList.vue'));
        case 'shoppable':
          return defineAsyncComponent(() => import('./Shoppable/Shoppable.vue'));

        default:
          return defineAsyncComponent(() => import('./TagboardList.vue'));
      }
    },
  },

  mounted() {
    this.contentTabsResizeObserver = new ResizeObserver(() => this.handleScroll());
    this.contentTabsResizeObserver.observe(this.$refs.contentTabs);
    this.handleScroll();
  },

  beforeUnmount() {
    this.contentTabsResizeObserver?.disconnect();
  },

  methods: {
    scrollMenuLeft() {
      const menuContainer = this.$refs.contentTabs;
      menuContainer.scrollLeft -= 150; // Adjust this value as needed
    },

    scrollMenuRight() {
      const menuContainer = this.$refs.contentTabs;
      menuContainer.scrollLeft += 150; // Adjust this value as needed
    },

    handleScroll() {
      const { contentTabs } = this.$refs;

      if (!contentTabs) {
        return;
      }

      this.contentTabOverflow = contentTabs.scrollWidth > contentTabs.clientWidth;
      this.contentTabOverflowButtonsDisabled = [
        contentTabs.scrollLeft !== 0,
        contentTabs.scrollLeft < contentTabs.scrollWidth - contentTabs.clientWidth,
      ];
    },
  },

  watch: {
    hasShoppable() {
      this.$nextTick(this.handleScroll);
    },
  },
};
</script>
